import { ShowName } from './hypertrackerTypes';

/** Decimal value serialized as a string. Do not convert to `number` for financial calculations. */
export type DecimalString = string;

export enum ALPHAFEED_VISIBILITY {
    LIVE = 'LIVE',
    DELAYED = 'DELAYED',
}

export interface AlphaFeedVisibility {
    mode: ALPHAFEED_VISIBILITY;
    /** ISO-8601 timestamp */
    visibleThrough: string;
    /** 0 (LIVE) or 1800 (DELAYED) */
    delaySeconds: number;
}

export interface AlphaFeedSummaryResponse {
    /** Completion time of the latest successful full scan. */
    poolRefreshedAt: string | null;
    /** Net realised PNL of featured positions closed in the last 24h; counter PNL sign-reversed. */
    pnl24Hours: DecimalString;
    qualifiedWallets: {
        count: string;
        added: string;
        removed: string;
    };
    positionEvents: {
        /** Opens, increases, reductions, and closes in the rolling last 24 hours. */
        last24h: string;
        /** The immediately preceding 24-hour window. */
        previous24h: string;
    };
}

export type AlphaFeedSegmentId =
    | 'top_scalper'
    | 'counter_scalper'
    | 'top_day_trader'
    | 'counter_day_trader';

/** Metric population keys currently returned (`top_day_trader`: 30d/7d, others: 7d/72h). */
export type AlphaFeedMetricKey = '30d' | '7d' | '72h';

/** Segment-specific coin metrics. Every field is always present; `null` = not exposed yet. */
export interface AlphaFeedTradingMetric {
    tradeCount: string | null;
    realisedPnlUsd: DecimalString | null;
    /** Ratio, `1` = 100%. */
    winRate: DecimalString | null;
    roundTripVolumeUsd: DecimalString | null;
    medianSizeUsd: DecimalString | null;
    grossWinningPnlUsd: DecimalString | null;
    grossLosingPnlAbsUsd: DecimalString | null;
    /** Gross wins / abs gross losses. The string `"Infinity"` when there are no losing trades. */
    profitFactor: DecimalString | null;
}

export interface AlphaFeedPositionQualification {
    alphaSegmentId: AlphaFeedSegmentId;
    ruleSetVersion: string;
    qualificationRunId: string;
    qualifiedAt: string;
    startedAt: string;
    /** Keyed by population, currently `AlphaFeedMetricKey`; future keys (e.g. `latest10`) may appear. */
    metrics: Record<string, AlphaFeedTradingMetric>;
}

export type AlphaFeedPositionAction = 'open' | 'increase' | 'reduce' | 'close';

export type AlphaFeedPositionStatus = 'open' | 'closed';

export interface AlphaFeedPositionChange {
    executionPrice: DecimalString;
    markPrice: DecimalString;
    /** Positive for open/increase; negative for reduce/close. */
    size: DecimalString;
    notionalUsd: DecimalString;
    realisedPnlUsd: DecimalString | null;
}

export interface AlphaFeedParticipantCounts {
    follow: number;
    counter: number;
}

interface AlphaFeedPositionBase {
    positionId: string;
    /** Empty string for the default dex. */
    dex: string;
    coin: string;
    side: 'long' | 'short';
    trader: {
        showName?: ShowName | null;
        /** Null unless a separate entitlement permits disclosure. */
        address: string | null;
        knownTrader?: boolean;
    };
    /** Qualification captured when the position first entered AlphaFeed. */
    qualification: AlphaFeedPositionQualification;
    openedAt: string;
    latestActivityAt: string;

    size: DecimalString;
    notionalUsd: DecimalString;
    averageEntryPrice: DecimalString;
    leverage: number | null;
    marginMode: string | null;

    entrySize: DecimalString;
    entryNotionalUsd: DecimalString;
    exitSize: DecimalString;
    exitNotionalUsd: DecimalString;
    peakSize: DecimalString;
    peakNotionalUsd: DecimalString;
    grossRealisedPnlUsd: DecimalString;
    feesUsd: DecimalString;
    participantCounts?: AlphaFeedParticipantCounts;
}

interface AlphaFeedOpenPositionSnapshot extends AlphaFeedPositionBase {
    status: 'open';
}

interface AlphaFeedClosedPositionSnapshot extends AlphaFeedPositionBase {
    status: 'closed';
    closedAt: string;
    averageExitPrice: DecimalString;
    fundingUsd: DecimalString | null;
    netRealisedPnlUsd: DecimalString | null;
}

/** Complete position state without `latestEvent`. */
export type AlphaFeedPositionSnapshot =
    | AlphaFeedOpenPositionSnapshot
    | AlphaFeedClosedPositionSnapshot;

export interface AlphaFeedPositionEvent {
    eventId: string;
    eventTime: string;
    action: AlphaFeedPositionAction;
    /** Extensible string: do not implement exhaustive UI logic over this field. */
    cause: string;
    change: AlphaFeedPositionChange;
    /** Complete position state immediately after this event. */
    position: AlphaFeedPositionSnapshot;
}

/** Compact event embedded in a position: no repeated `position` snapshot. */
export type AlphaFeedLatestPositionEvent = Omit<AlphaFeedPositionEvent, 'position'>;

export type AlphaFeedOpenPosition = AlphaFeedOpenPositionSnapshot & {
    latestEvent: AlphaFeedLatestPositionEvent;
};

export type AlphaFeedClosedPosition = AlphaFeedClosedPositionSnapshot & {
    latestEvent: AlphaFeedLatestPositionEvent;
};

export type AlphaFeedPosition = AlphaFeedOpenPosition | AlphaFeedClosedPosition;

export interface AlphaFeedPositionsResponse {
    positions: AlphaFeedPosition[];
    cursor: string | null;
    visibility: AlphaFeedVisibility;
}

export interface AlphaFeedPositionResponse {
    position: AlphaFeedPosition;
    visibility: AlphaFeedVisibility;
}

export interface AlphaFeedPositionEventsResponse {
    events: AlphaFeedPositionEvent[];
    cursor: string | null;
    visibility: AlphaFeedVisibility;
}

export type AlphaFeedEventsOrder = 'asc' | 'desc';

export interface AlphaFeedPositionsParams {
    status?: AlphaFeedPositionStatus;
    coin?: string;
    /** 1 to 100, default 20 */
    limit?: number;
    cursor?: string;
}

export interface AlphaFeedPositionEventsParams {
    /** Default `asc` (oldest first) */
    order?: AlphaFeedEventsOrder;
    /** 1 to 500, default 100 */
    limit?: number;
    cursor?: string;
}

interface AlphaFeedPulseEventBase {
    /** Stable Pulse identity, e.g. `trade:42` or `position:42:top_scalper`. */
    eventId: string;
    eventTime: string;
    sourcePositionId: string;
    alphaSegmentId: AlphaFeedSegmentId;
    coin: string;
    side: 'long' | 'short';
}

export type AlphaFeedPulseEvent =
    | (AlphaFeedPulseEventBase & { eventType: 'initial_follow' | 'initial_counter' })
    | (AlphaFeedPulseEventBase & {
          eventType: 'position_closed';
          /** Whole-position PNL, including partial exits. */
          totalPnlUsd: DecimalString;
      });

/** `GET alpha-feed/pulse` */
export interface AlphaFeedPulseResponse {
    events: AlphaFeedPulseEvent[];
    cursor: string | null;
    visibility: AlphaFeedVisibility;
}

export interface AlphaFeedPulseParams {
    /** 1 to 100, default 20 */
    limit?: number;
    cursor?: string;
}

export interface AlphaFeedQualificationTrade {
    realisedPnlUsd: DecimalString;
    closedAt: string;
}

export interface AlphaFeedQualificationTradesResponse {
    trades: AlphaFeedQualificationTrade[];
    window: AlphaFeedMetricKey;
    from: string;
    to: string;
}

export interface AlphaFeedTraderOutcome {
    outcome: 'win' | 'loss';
    closedAt: string;
}

export interface AlphaFeedRecentOutcomesResponse {
    trades: AlphaFeedTraderOutcome[];
}

export interface AlphaFeedSsePositionEvent {
    eventId: string;
    eventType: 'position_change';
    eventTime: string;
    emittedAt: string;
    action: AlphaFeedPositionAction;
    cause: string;
    change: AlphaFeedPositionChange;
    /** Same snapshot as REST events; `trader.address` is always `null` on SSE. */
    position: AlphaFeedPositionSnapshot;
}

export interface AlphaFeedSseData {
    /** A frame may contain several events. */
    events: AlphaFeedSsePositionEvent[];
}

export type AlphaFeedTradingNetwork = 'testnet' | 'mainnet';

/** `GET trading/config` */
export interface AlphaFeedTradingConfig {
    network: AlphaFeedTradingNetwork;
    builderAddress: string;
    /** Tenths of a basis point (`60` = 0.06%). */
    builderFee: number;
    /** Percentage string shown to the user and signed in `approveBuilderFee`, e.g. `"0.06%"`. */
    builderMaxFeeRate: string;
    /** Max slippage in basis points when the client sends none (`300` = 3%). */
    defaultSlippageBps: number;
    /** Highest `maxSlippageBps` the backend accepts, in basis points (`300` = 3%). */
    maxSlippageBps: number;
    /** Rolling 30d builder volume (USD) at which a wallet trades at `reducedBuilderFee`. */
    reducedFeeThresholdVolumeUsd: DecimalString;
    /** Tenths of a basis point (`20` = 0.02%). */
    reducedBuilderFee: number;
}

/** `POST trading/agent` */
export interface AlphaFeedTradingAgent {
    accountAddress: string;
    agentAddress: string;
    /** Not proof of approval; Hyperliquid `/info` is authoritative. */
    agentStatus: 'created' | 'approved' | 'revoked';
    agentName: string;
}

/** Body of `POST trading/positions/close`; closes any connected-wallet perp position. */
export interface CloseTradingPositionRequest {
    address: string;
    /** Canonical Hyperliquid perp name; HIP-3 coins include their DEX prefix. */
    coin: string;
    /** Exact absolute position size; the server rejects it with 409 if the position changed. */
    size: DecimalString;
}

/** A whole-position close; `side` is only used for the result toast, not sent. */
export interface PositionCloseIntent extends CloseTradingPositionRequest {
    side: 'long' | 'short';
}

/** `POST trading/positions/close` response. `status` values are not documented yet. */
export interface CloseTradingPositionResponse {
    cloid: string;
    status: string;
    coin: string;
    direction: 'long' | 'short';
    size: DecimalString;
    /** Nullability is not documented, so a missing value is handled like `null`. */
    filledSize?: DecimalString | null;
    averagePrice?: DecimalString | null;
    orderId?: string | null;
}

export type AlphaFeedTradingMode = 'follow' | 'counter';

/** Active follow/counter lifecycle of a connected wallet position. */
export interface AlphaFeedTradingLifecycle {
    lifecycleId: string;
    address: string;
    /** Canonical Hyperliquid coin; HIP-3 values already include the DEX prefix. */
    coin: string;
    positionSide: 'long' | 'short';
    startedAt: string;
    /** Newest attached trade for this user, regardless of status. Shape is not documented yet. */
    latestTrade: unknown;
    source: {
        positionId: string;
        linkedEventId: string;
        mode: AlphaFeedTradingMode;
        linkedAt: string;
        trader: { alias: string | null; address: string | null };
        qualification: AlphaFeedPositionQualification | null;
        latestEvent: AlphaFeedLatestPositionEvent | null;
    };
}

/** `GET alpha-feed/trading-lifecycles?address=` (active lifecycles only) */
export interface AlphaFeedTradingLifecyclesResponse {
    lifecycles: AlphaFeedTradingLifecycle[];
}

/** `alphaFeedTradingLifecycle` SSE frame. */
export interface AlphaFeedTradingLifecycleSseData {
    eventType: 'trading_lifecycle_changed';
    eventId: string;
    lifecycleId: string;
    tradeId: string | null;
    address: string;
    coin: string;
    /** `lifecycle_closed` = the user's position became flat or flipped side. */
    change: 'trade_created' | 'trade_updated' | 'lifecycle_closed' | 'lifecycle_deleted';
    occurredAt: string;
}

/** Rolling builder-volume reward state of one connected account. */
export interface AlphaFeedRewardAccount {
    address: string;
    window: '30d';
    volumeUsd: DecimalString;
    unlockThresholdVolumeUsd: DecimalString;
    remainingVolumeUsd: DecimalString;
    isUnlocked: boolean;
    /** Null until the volume processor has a projection for the address (treated as locked, $0). */
    calculatedAt: string | null;
    nextEvaluationAt: string | null;
}

/** `GET alpha-feed/rewards`. */
export interface AlphaFeedRewardsResponse {
    builderAddress: string;
    /** True when at least one account is unlocked: trader addresses are revealed. */
    isAlphaFeedUnlocked: boolean;
    accounts: AlphaFeedRewardAccount[];
}

/** `alphaFeedRewardStatus` SSE frame: latest projection for one account. */
export interface AlphaFeedRewardStatusSseData extends Omit<
    AlphaFeedRewardAccount,
    'remainingVolumeUsd'
> {
    eventType: 'alpha_feed_reward_status_updated';
    eventId: string;
    builder: string;
}

/** Hyperliquid `/info` `extraAgents` item. */
export interface HyperliquidExtraAgent {
    name: string;
    address: string;
    /** Unix ms. */
    validUntil: number;
}

export type AlphaFeedWalletTradingStatus =
    | { kind: 'ready' | 'needsAgent' | 'needsFee'; agent: AlphaFeedTradingAgent }
    /** Backend or Hyperliquid failure; never treated as "not approved". */
    | { kind: 'error' };

export type AlphaFeedTradeMode = 'follow' | 'counter';

export type AlphaFeedMarginMode = 'cross' | 'isolated';

export type AlphaFeedTradeExecutionStatus = 'pending' | 'succeeded' | 'failed' | 'unknown';

/** Body of `POST alpha-feed/positions/:positionId/segments/:segmentId/events/:eventId/trades`. */
export interface CreateAlphaFeedTradeRequest {
    mode: AlphaFeedTradeMode;
    accountAddress: string;
    /** Base-asset quantity; the backend normalizes it. */
    size: DecimalString;
    /** Omitted on a reduce; the backend rejects it there (also marginMode, tp, sl). */
    leverage?: number;
    marginMode?: AlphaFeedMarginMode;
    maxSlippageBps?: number;
    tp?: { triggerPrice: DecimalString };
    sl?: { triggerPrice: DecimalString };
}

/** User trade intent; `executionStatus` (not the HTTP status) tells whether it filled. */
export interface AlphaFeedTrade {
    tradeId: string;
    lifecycleId: string | null;
    executionStatus: AlphaFeedTradeExecutionStatus;
    accountAddress: string;
    source: {
        positionId: string;
        alphaSegmentId: AlphaFeedSegmentId;
        eventId: string;
        eventTime: string;
        side: 'long' | 'short';
        action: AlphaFeedPositionAction;
    };
    trade: {
        mode: AlphaFeedTradeMode;
        action: 'open' | 'close';
        dex: string;
        asset: string;
        direction: 'long' | 'short';
        requestedSize: DecimalString;
        submittedSize: DecimalString | null;
        filledSize: DecimalString | null;
        leverage: number | null;
        marginMode: AlphaFeedMarginMode | null;
        maxSlippageBps: number | null;
        tpTriggerPrice: DecimalString | null;
        slTriggerPrice: DecimalString | null;
    };
    execution: {
        cloid: string;
        orderId: string | null;
        averagePrice: DecimalString | null;
        tradingServiceStatus: 'filled' | 'error' | 'unknown' | null;
        error: string | null;
    };
    createdAt: string;
    completedAt: string | null;
}

/** Hyperliquid `/info` `clearinghouseState` position (numbers arrive as strings). */
export interface HlAssetPosition {
    coin: string;
    /** Signed size: positive long, negative short. */
    szi: string;
    entryPx: string;
    leverage: { type: AlphaFeedMarginMode; value: number };
    liquidationPx?: string | null;
    unrealizedPnl: string;
    positionValue: string;
    /** Positive means the user paid funding. */
    cumFunding?: { sinceOpen?: string };
    /** Unvalidated. Isolated: the position's equity. Cross: its initial margin. */
    marginUsed?: unknown;
    /** Unvalidated. The position's own max leverage; may differ from the current `meta`. */
    maxLeverage?: unknown;
}

/** The user's live Hyperliquid position on the trading network, parsed for display. */
export interface TradeUserPosition {
    side: 'long' | 'short';
    /** Hyperliquid's `szi` without its sign. */
    sizeAbs: string;
    entryPrice: number;
    liquidationPrice: number | null;
    leverage: number;
    marginMode: AlphaFeedMarginMode;
    valueUsd: number;
    /** Positive means the user received funding. */
    fundingUsd: number;
    unrealizedPnlUsd: number;
    marginUsedUsd: number | null;
    maxLeverage: number | null;
}

export interface HlClearinghouseState {
    assetPositions: { position: HlAssetPosition }[];
    marginSummary: { accountValue: string };
    /** Unvalidated. */
    crossMarginSummary?: { accountValue?: unknown };
    /** Unvalidated. */
    crossMaintenanceMarginUsed?: unknown;
}

export interface HlBboLevel {
    px: DecimalString;
    sz: DecimalString;
    n: number;
}

/** Hyperliquid websocket `bbo`: `[best bid, best ask]`, pushed only when either changes. */
export interface HlBbo {
    coin: string;
    time: number;
    bbo: [HlBboLevel, HlBboLevel];
}

/** Hyperliquid `/info` `activeAssetData`. Arrays are `[buy, sell]`. */
export interface HlActiveAssetData {
    leverage: { type: AlphaFeedMarginMode; value: number };
    maxTradeSzs: [string, string];
    markPx: string;
}

export interface HlMetaAsset {
    name: string;
    szDecimals: number;
    maxLeverage: number;
    onlyIsolated?: boolean;
    /** Unvalidated. */
    marginTableId?: unknown;
}

export interface HlMeta {
    universe: HlMetaAsset[];
    /** Unvalidated `[id, { marginTiers }]` tuples. */
    marginTables?: unknown;
    /** Unvalidated. */
    collateralToken?: unknown;
}

/** A Hyperliquid margin tier: `maxLeverage` applies from `lowerBound` notional (USD). */
export interface HlMarginTier {
    lowerBound: number;
    maxLeverage: number;
}

/** The coin's `meta` asset with its margin tiers and its dex's collateral token. */
export type TradeMetaAsset = HlMetaAsset & {
    marginTiers: HlMarginTier[];
    /** Null when `meta` has no valid collateral token. */
    collateralToken: number | null;
};

/** Error bodies observed from the AlphaFeed API. */
export type AlphaFeedApiError =
    | { message: string; error: string; statusCode: number }
    | { message: 'validation error'; errors: { property: string; errors: string[] }[] };

export type AlphaFeedPulseTraderVariant = 'topPerformer' | 'spiraling';

/** A ready trading wallet as listed in the AlphaFeed wallet selectors. */
export interface AlphaFeedWalletAccount {
    id: string;
    label: string;
    balance: number;
}
